import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useState } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetOtcRevenueChartDataQuery } from 'src/store/services/analytics.service';

import { DEFAULT_TIME_PERIOD, TIME_PERIOD_OPTIONS } from '../constants/timePeriods';

export function OTCRevenueChart() {
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_TIME_PERIOD);
  const { address, network } = useAnalyticsContext();

  const {
    data: chartData,
    isLoading,
    error,
  } = useGetOtcRevenueChartDataQuery(
    {
      period: selectedPeriod as any,
      size: 50,
      address,
      network,
    },
    { skip: !address || !network },
  );

  const handlePeriodChange = (newPeriod: string) => {
    setSelectedPeriod(newPeriod);
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'area',
      background: 'transparent',
      toolbar: {
        show: false,
      },
      fontFamily: 'Gotham',
    },
    theme: {
      mode: 'dark',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      colors: ['#4CC3C1'],
      width: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: '#4CC3C1',
            opacity: 0.5,
          },
          {
            offset: 100,
            color: '#4CC3C1',
            opacity: 0,
          },
        ],
      },
    },
    grid: {
      show: true,
      borderColor: 'rgba(255, 255, 255, 0.03)',
      strokeDashArray: 6,
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    xaxis: {
      categories: chartData?.result?.map(item => item.s) || [],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      tickAmount: 6,
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham',
          fontWeight: 400,
        },
        formatter: (value: number) => `$${value}`,
      },
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham',
      },
    },
  };

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.white,
            fontFamily: 'Gotham',
          }}>
          Total Revenue via OTC
        </Typography>

        <FormControl size='small'>
          <Select
            value={selectedPeriod}
            onChange={e => handlePeriodChange(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '4px',
              color: colors.white,
              fontSize: '12px',
              fontFamily: 'Gotham',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-icon': {
                color: colors.white,
              },
            }}>
            {TIME_PERIOD_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 280 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.neutral90 }}>Loading chart data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load chart data</Typography>
          </Box>
        ) : (
          <Chart
            options={chartOptions}
            series={[{ name: 'OTC Revenue', data: chartData?.result?.map(item => item.y) || [] }]}
            type='area'
            height='100%'
          />
        )}
      </Box>
    </Box>
  );
}
